import React, { useState } from "react";
import { Box, Button, Typography, Grid, Checkbox, FormControlLabel, TextField, Divider } from "@mui/material";
import { Modal } from "../Modal";
import UploadIcon from "@mui/icons-material/Upload";
import DownloadIcon from "@mui/icons-material/Download";
import PriceChangeIcon from "@mui/icons-material/PriceChange";
import { toast } from "react-hot-toast";

interface ModalBulkUpdatePricingProps {
  open: boolean;
  onClose: () => void;
  onImport: (formData: FormData) => Promise<void>;
  isLoading?: boolean;
  onExport: () => Promise<void>;
  isExporting?: boolean;
}

export const ModalBulkUpdatePricing: React.FC<ModalBulkUpdatePricingProps> = ({
  open,
  onClose,
  onImport,
  isLoading = false,
  onExport,
  isExporting = false,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUpdateImmediately, setIsUpdateImmediately] = useState<boolean>(true);
  const [applyTime, setApplyTime] = useState<string>("");

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const handleSubmit = async () => {
    if (!selectedFile) {
      toast.error("Please select a file to import");
      return;
    }
    const formData = new FormData();
    formData.append("file", selectedFile, selectedFile.name);
    formData.append("is_update_immediately", String(isUpdateImmediately));

    if (!isUpdateImmediately && applyTime) {
      const applyDate = new Date(applyTime);
      formData.append("apply_time", applyDate.toISOString());
    }

    await onImport(formData);
    setSelectedFile(null); // Reset after import
    setIsUpdateImmediately(true);
    setApplyTime("");
  };

  const handleClose = () => {
    setSelectedFile(null);
    setIsUpdateImmediately(true);
    setApplyTime("");
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      name="Bulk Update Pricing"
      description=""
      icon={<PriceChangeIcon fontSize="large" color="primary" />}
    >
      <Box p={2}>
        <Grid container direction="column" spacing={3}>
          <Grid item>
            <Typography variant="h6" fontSize="16px" fontWeight="600" mb={1}>
              1. Download Template
            </Typography>
            <Typography variant="body2" color="textSecondary" mb={2}>
              Export current charge points pricing data as an Excel template. You can modify the price values in the exported file and upload it below.
            </Typography>
            <Button
              variant="outlined"
              color="primary"
              onClick={onExport}
              disabled={isExporting}
              startIcon={<DownloadIcon />}
            >
              Export Pricing
            </Button>
          </Grid>

          <Grid item>
            <Divider />
          </Grid>

          <Grid item>
            <Typography variant="h6" fontSize="16px" fontWeight="600" mb={1}>
              2. Upload Pricing Data
            </Typography>
            <Typography variant="body2" color="textSecondary" mb={2}>
              Select the modified Excel file (.xlsx, .xls) to import. Make sure the file format matches the exported template.
            </Typography>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="flex-start"
              gap={2}
              p={2}
              border="1px dashed #ccc"
              borderRadius="8px"
              mb={3}
            >
              <Button variant="outlined" component="label" startIcon={<UploadIcon />}>
                Choose File
                <input type="file" accept=".xlsx, .xls" hidden onChange={handleFileChange} />
              </Button>
              <Typography variant="body2" color={selectedFile ? "textPrimary" : "textSecondary"}>
                {selectedFile ? selectedFile.name : "No file selected"}
              </Typography>
            </Box>

            <FormControlLabel
              control={
                <Checkbox
                  checked={isUpdateImmediately}
                  onChange={(e) => setIsUpdateImmediately(e.target.checked)}
                  color="primary"
                />
              }
              label="Update and sync immediately"
            />

            {!isUpdateImmediately && (
              <Box mt={2}>
                <TextField
                  label="Apply time"
                  type="datetime-local"
                  fullWidth
                  value={applyTime}
                  onChange={(e) => setApplyTime(e.target.value)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Box>
            )}
          </Grid>
          <Grid item>
            <Box display="flex" justifyContent="flex-end" gap={2} mt={2}>
              <Button onClick={handleClose} disabled={isLoading} variant="outlined" color="secondary">
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={!selectedFile || isLoading || (!isUpdateImmediately && !applyTime)}
                variant="contained"
                color="primary"
                startIcon={<UploadIcon />}
              >
                Submit
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Modal>
  );
};
